import {
  EmailTemplateReadable,
  mailboxesTemplatesCreateMutation,
  mailboxesTemplatesDestroyMutation,
  mailboxesTemplatesListOptions,
  mailboxesTemplatesListQueryKey,
  mailboxesTemplatesPartialUpdateMutation,
  mailboxesTemplatesRetrieveOptions
} from '@crm/api'
import { ConfirmModal } from '@crm/components'
import {
  FormEmailTemplate,
  FormModal
} from '@crm/features/nomenclatures/components'
import { defaultFilters } from '@crm/utils'
import { TableActions, TableContainer } from '@shared/components'
import {
  useInvalidatePredicateQueries,
  useQueryParams,
  useResetPagination
} from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { DATE_TIME_FORMAT, format, showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { App, Button, Flex } from 'antd'
import { Add, Edit2, Trash } from 'iconsax-react'
import { useCallback, useMemo, useState } from 'react'

import type { MenuProps, TableColumnsType } from 'antd'

const formName = 'e-mail-templates'

export const NomenclaturesEmailTemplates = () => {
  const { t } = useLocales()
  const params = useQueryParams()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { resetPagination } = useResetPagination()
  const { notification } = App.useApp()

  const [isCreate, setIsCreate] = useState(false)
  const [editItem, setEditItem] = useState<number>()
  const [deleteItem, setDeleteItem] = useState<number>()

  const { data, isLoading } = useQuery(
    mailboxesTemplatesListOptions({
      query: {
        ...defaultFilters,
        ...params
      }
    })
  )

  const { mutate } = useMutation({
    ...mailboxesTemplatesDestroyMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      invalidateQueries([mailboxesTemplatesListQueryKey()])
      resetPagination()
      setDeleteItem(undefined)
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
    }
  })

  const onDelete = () => {
    if (deleteItem) mutate({ path: { id: deleteItem } })
  }

  const onCancelDelete = () => setDeleteItem(undefined)

  const onCloseModal = () => {
    setEditItem(undefined)
    setIsCreate(false)
  }

  const getActionColumnItem: (id: number) => MenuProps['items'] = useCallback(
    (id) => [
      {
        label: t('account_settings.edit'),
        key: 'edit',
        icon: <Edit2 size={20} />,
        onClick: () => setEditItem(id)
      },
      { type: 'divider' },
      {
        label: t('account_settings.delete'),
        key: 'delete',
        danger: true,
        icon: <Trash size={20} />,
        onClick: () => setDeleteItem(id)
      }
    ],
    [t]
  )

  const columns: TableColumnsType<EmailTemplateReadable> = useMemo(
    () => [
      {
        title: t('account_settings.title'),
        dataIndex: 'title'
      },
      {
        title: t('account_settings.privacy'),
        render: ({ is_public }) =>
          t(is_public ? 'account_settings.public' : 'account_settings.private')
      },
      {
        title: t('account_settings.modified_at'),
        render: ({ modified_at }) => format(modified_at, DATE_TIME_FORMAT)
      },
      {
        width: 50,
        fixed: 'right',
        render: ({ id }) => <TableActions options={getActionColumnItem(id)} />
      }
    ],
    [t, getActionColumnItem]
  )

  return (
    <Flex vertical gap={16}>
      <TableContainer
        rowKey="id"
        loading={isLoading}
        data={data}
        columns={columns}
        showSorterTooltip={false}
        rightChildren={
          <Button
            type="primary"
            icon={<Add size={20} />}
            onClick={() => setIsCreate(true)}
          >
            {t('nomenclature.add_email_template')}
          </Button>
        }
        bordered
      />

      <ConfirmModal
        open={!!deleteItem}
        onSuccess={onDelete}
        onCancel={onCancelDelete}
      />

      <FormModal
        title={t(
          editItem
            ? 'account_settings.update_email_template'
            : 'account_settings.create_email_template'
        )}
        id={editItem}
        formName={formName}
        open={!!(editItem || isCreate)}
        getItem={mailboxesTemplatesRetrieveOptions}
        onCreate={mailboxesTemplatesCreateMutation}
        onUpdate={mailboxesTemplatesPartialUpdateMutation}
        onCloseModal={onCloseModal}
        width={800}
      >
        <FormEmailTemplate />
      </FormModal>
    </Flex>
  )
}
